import { useQuery } from "@tanstack/react-query";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { decryptPersonData } from "@/shared/utils/serverEncryption";

interface UseDeceasedPassportDataResult {
  deceasedPassportNumber: string | null;
  isLoading: boolean;
}

export function useDeceasedPassportData(): UseDeceasedPassportDataResult {
  const { person } = useAppContext();

  const { data: deceasedPassportNumber, isLoading } = useQuery({
    queryKey: ["deceasedPassportData", person?.estate_id],
    queryFn: async () => {
      if (!person?.estate_id) return null;

      const { data: personData, error } = await supabase
        .from("estates")
        .select("passport_number_encrypted")
        .eq("estate_id", person.estate_id)
        .single();

      if (error || !personData?.passport_number_encrypted) return null;

      try {
        return await decryptPersonData(
          person.estate_id,
          personData.passport_number_encrypted,
          "passport_number",
        );
      } catch {
        return null;
      }
    },
    enabled: !!person?.estate_id,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });

  return {
    deceasedPassportNumber: deceasedPassportNumber || null,
    isLoading,
  };
}
